import { FundingSourceType } from "@wingspanhq/payments/dist/interfaces";
import { Currency } from "@wingspanhq/users/dist/lib/interfaces";
import {
  queriesInternalAccounts,
  queriesPayouts,
  queriesTaxWithholdings
} from "@query";
import { selectorOwnPayoutSettings } from "@selectors/selectorOwnPayoutSettings";
import { useWSMutation, WSMutationConfig } from "../../../../query/helpers";
import { useUserId } from "../../../../query/hooks/helpers";
import { QUERY_PAYROLL_SETTINGS } from "../../../../query/payments/keys";
import { QUERY_USERS_MEMBER_PROFILE } from "../../../../query/users/keys";
import { internalAccountsApi, payoutsApi, taxWithholdingsApi } from "@api";
import {
  AcknowledgementName,
  Currency as SDKCurrency,
  InternalAccount,
  InternalAccountType,
  PreferredPayoutSpeed,
  ResponseError
} from "@wingspanhq/wingspan-api-sdk";
import { paymentsService } from "../../../../services/payments";
import { updateAcknowledgements } from "../../../../shared/utils/updateAcknowledgements";

export const useMutationSubmit = (
  type: InternalAccountType,
  currency: SDKCurrency,
  acknowledgements: AcknowledgementName[],
  options?: {
    setFundingSource?: boolean;
    setStandardPayoutDestination?: boolean;
  },
  config?: WSMutationConfig<InternalAccount, ResponseError, unknown>
) => {
  const userId = useUserId();

  return useWSMutation(
    async () => {
      if (!Object.values(Currency).map(String).includes(currency)) {
        throw new Error("Currency is not supported");
      }

      // 1. Update acknowledgements
      await updateAcknowledgements(acknowledgements);

      // 2. Create internal account
      const internalAccount = await internalAccountsApi.createInternalAccount({
        internalAccountCreate: {
          type,
          currency
        }
      });

      // 3. Set usage of the internal account
      // 3.1 Payroll funding method
      if (options?.setFundingSource) {
        await paymentsService.payrollSettings.update(userId, {
          fundingSource: {
            fundingSourceCurrency: Currency[currency as keyof typeof Currency],
            fundingSourceId: internalAccount.id,
            fundingSourceType: FundingSourceType.InternalAccount
          }
        });
      }

      // 3.2 Payout method
      if (options?.setStandardPayoutDestination) {
        const payoutSettingsList =
          await queriesPayouts.listPayoutSettings.fetchQueryData();
        const payoutSettingsId =
          selectorOwnPayoutSettings(payoutSettingsList)?.id;

        if (payoutSettingsId) {
          await payoutsApi.updatePayoutSettings({
            payoutSettingsId,
            payoutSettingsUpdate: {
              preferredPayoutSpeed: PreferredPayoutSpeed.STANDARD,
              standardDestination: null
            }
          });
        }
      }

      // Enable withholdings only after the account is created, so the settings
      // are never enabled without an account present.
      if (type === InternalAccountType.TAX_WITHHOLDING) {
        await taxWithholdingsApi.updateTaxWithholdingSettings({
          taxWithholdingSettingsUpdate: { isEnabled: true }
        });
      }

      return internalAccount;
    },
    {
      dependencies: [
        QUERY_PAYROLL_SETTINGS,
        queriesPayouts.listPayoutSettings.queryKeyBase,
        queriesPayouts.getPayoutSettings.queryKeyBase,
        queriesInternalAccounts.getInternalAccount.queryKeyBase,
        queriesInternalAccounts.listInternalAccounts.queryKeyBase,
        queriesTaxWithholdings.getTaxWithholdingSettings.queryKeyBase,
        QUERY_USERS_MEMBER_PROFILE
      ],
      ...config
    }
  );
};
